import { useEffect } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/components/ui/states';
import {
  CampaignSlider,
  CategoryGrid,
  Editorial,
  Hero,
  InstagramWall,
  Intent,
  OccasionBanner,
  ProductRail,
  Reviews,
  Trust,
} from '@/features/home/home-sections';
import { useHome } from '@/hooks/useStorefront';
import { useStoreSettings } from '@/hooks/useStoreSettings';
import { applyPageMeta, applyStructuredData } from '@/lib/seo';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/services/api/client';
import type { DeliveryAvailability, HomeSection } from '@/types/storefront';

/**
 * ANA SAYFA
 *
 * Bölüm sırası ve içeriği veri tabanından gelir (tek composite istek).
 * Frontend bölüm sırasını bilmez — yönetici Faz 6'da değiştirdiğinde
 * kod değişmeyecek.
 */
export function HomePage() {
  const { data, isPending, isError, error, refetch } = useHome();
  const { data: settings } = useStoreSettings();

  /**
   * LocalBusiness şeması mağaza ayarlarından üretilir (sunucu tarafında).
   * Eksik alan uydurulmaz; ayar yoksa şema hiç basılmaz.
   */
  const { data: localBusiness } = useQuery({
    queryKey: ['seo', 'local-business'],
    queryFn: () => get<Record<string, unknown> | null>('/seo/local-business'),
    staleTime: 60 * 60 * 1000,
  });

  useEffect(() => {
    applyPageMeta({
      title:
        settings?.['seo.defaultTitle'] ??
        'Isparta Çiçekçi & Çiçek Siparişi | Hayal Çiçek',
      description:
        settings?.['seo.defaultDescription'] ??
        "Isparta'da buket, gül, orkide, saksı çiçeği, çelenk ve hediyelik siparişi. Uygun bölgelere aynı gün teslimat.",
      canonicalPath: '/',
    });

    if (localBusiness) {
      applyStructuredData(localBusiness);
    }

    return () => applyStructuredData(null);
  }, [settings, localBusiness]);

  if (isPending) {
    return <HomeSkeleton />;
  }

  if (isError) {
    return (
      <div className="container-page">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="pb-4">
      {data.sections.map((section) => (
        <SectionRenderer
          key={section.key}
          section={section}
          delivery={data.delivery}
        />
      ))}
    </div>
  );
}

function SectionRenderer({
  section,
  delivery,
}: {
  section: HomeSection;
  delivery: DeliveryAvailability | null;
}) {
  switch (section.type) {
    case 'HERO':
      return <Hero section={section} delivery={delivery} />;
    case 'INTENT':
      return <Intent section={section} />;
    case 'CAMPAIGN_SLIDER':
      return <CampaignSlider section={section} />;
    case 'PRODUCT_RAIL':
      return <ProductRail section={section} delivery={delivery} />;
    case 'EDITORIAL':
      return <Editorial section={section} />;
    case 'CATEGORY_GRID':
      return <CategoryGrid section={section} />;
    case 'REVIEWS':
      return <Reviews section={section} />;
    case 'INSTAGRAM':
      return <InstagramWall section={section} />;
    case 'TRUST':
      return <Trust section={section} />;
    case 'OCCASION_BANNER':
      return <OccasionBanner section={section} />;
    default:
      // Bilinmeyen bölüm tipi sessizce atlanır: yönetici yeni bir tip
      // eklediğinde eski istemci çökmez.
      return null;
  }
}

/** İskelet gerçek düzenin ta kendisi: hero + şerit + ray. */
function HomeSkeleton() {
  return (
    <div>
      <Skeleton className="h-[86svh] min-h-[520px] w-full rounded-none sm:h-[78svh]" />

      <div className="container-page mt-16">
        <Skeleton className="h-7 w-64" />
        <div className="rail mt-6 gap-3">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton
              key={index}
              className="aspect-[4/3] w-[42%] rounded-lg md:w-[18%]"
            />
          ))}
        </div>
      </div>

      <div className="container-page mt-16">
        <Skeleton className="h-7 w-56" />
        <div className="rail mt-6 gap-4">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="w-[62%] sm:w-[34%] lg:w-[21%]">
              <Skeleton className="aspect-4/5 w-full rounded-lg" />
              <Skeleton className="mt-3 h-4 w-4/5" />
              <Skeleton className="mt-2 h-4 w-1/3" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
